import React from "react";

const toNumber = (value) => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : 0;
};

const formatNumber = (value, decimals = 0) => {
  const numericValue = toNumber(value);
  return numericValue.toLocaleString(undefined, {
    maximumFractionDigits: decimals,
    minimumFractionDigits: decimals,
  });
};

const formatPercent = (value) => `${formatNumber(value, 0)}%`;

const getTotal = (rows, key) => rows.reduce((total, row) => total + toNumber(row[key]), 0);

const isFeatureEnabled = (value) => value === true || value === "true" || value === 1 || value === "1";

const getWeightedAverage = (rows, valueKey, weightKey) => {
  const totalWeight = getTotal(rows, weightKey);
  if (totalWeight <= 0) {
    return 0;
  }

  return rows.reduce((total, row) => total + toNumber(row[valueKey]) * toNumber(row[weightKey]), 0) / totalWeight;
};

const getPoints = (rows, key) => rows.map((row) => toNumber(row[key]));

const buildSparklinePath = (points, width = 132, height = 42) => {
  if (!points || points.length === 0) {
    return "";
  }

  if (points.length === 1) {
    return `M 0 ${height / 2} L ${width} ${height / 2}`;
  }

  const min = Math.min(...points);
  const max = Math.max(...points);
  const range = max - min || 1;
  const step = width / (points.length - 1);

  return points
    .map((point, index) => {
      const x = index * step;
      const y = height - ((point - min) / range) * (height - 8) - 4;
      return `${index === 0 ? "M" : "L"} ${x.toFixed(1)} ${y.toFixed(1)}`;
    })
    .join(" ");
};

const MiniSparkline = ({ points, color }) => {
  const path = buildSparklinePath(points);

  return (
    <svg className="assetMetricSparkline" viewBox="0 0 132 42" preserveAspectRatio="none" aria-hidden="true">
      <path className="assetMetricSparklineBase" d="M 0 36 L 132 36" />
      {path && <path className="assetMetricSparklineLine" d={path} style={{ stroke: color }} />}
    </svg>
  );
};

const MiniBars = ({ points, color }) => {
  const visiblePoints = points.slice(-14);
  const max = Math.max(...visiblePoints, 1);
  const barWidth = 132 / Math.max(visiblePoints.length, 1);

  return (
    <svg className="assetMetricBars" viewBox="0 0 132 42" preserveAspectRatio="none" aria-hidden="true">
      {visiblePoints.map((point, index) => {
        const height = Math.max(3, (toNumber(point) / max) * 34);
        return (
          <rect
            key={`${point}-${index}`}
            x={(index * barWidth + 2).toFixed(1)}
            y={(40 - height).toFixed(1)}
            width={Math.max(3, barWidth - 4).toFixed(1)}
            height={height.toFixed(1)}
            rx="2"
            style={{ fill: color }}
          />
        );
      })}
    </svg>
  );
};

const ProgressChart = ({ value, color }) => {
  const safeValue = Math.max(0, Math.min(100, toNumber(value)));

  return (
    <div className="assetMetricProgress" aria-hidden="true">
      <span style={{ width: `${safeValue}%`, background: color }} />
    </div>
  );
};

const SignalQualityChart = ({ gsmLoss, gpsLoss }) => {
  const averageLoss = (toNumber(gsmLoss) + toNumber(gpsLoss)) / 2;
  const quality = Math.max(0, Math.min(100, 100 - averageLoss));
  const color = averageLoss >= 20 ? "#cf2f45" : averageLoss >= 10 ? "#c07a16" : "#1f8a5b";

  return (
    <div className="assetMetricSignalChart" aria-hidden="true">
      <div className="assetMetricSignalBars">
        <span>
          <strong>GSM</strong>
          <i style={{ width: `${Math.max(0, Math.min(100, toNumber(gsmLoss)))}%`, background: color }} />
        </span>
        <span>
          <strong>GPS</strong>
          <i style={{ width: `${Math.max(0, Math.min(100, toNumber(gpsLoss)))}%`, background: color }} />
        </span>
      </div>
      <div className="assetMetricQualityBar">
        <span style={{ width: `${quality}%`, background: color }} />
      </div>
    </div>
  );
};

const MetricTile = ({ accent, icon, label, value, detail, children, variant }) => (
  <div className={`assetSummaryMetricTile ${variant || ""}`} style={{ borderTopColor: accent }}>
    <div className="assetMetricTopline">
      <span className="assetMetricIcon" style={{ color: accent }}>
        <i className={`fa ${icon}`} />
      </span>
      <span>{label}</span>
    </div>
    <div className="assetMetricValue">{value}</div>
    <div className="assetMetricDetail">{detail}</div>
    <div className="assetMetricChart">{children}</div>
  </div>
);

function GroupSummaryMetricTiles({ reportDetails }) {
  if (!Array.isArray(reportDetails) || reportDetails.length === 0) {
    return null;
  }

  const rows = reportDetails;
  const assetCount = rows.length;
  const activeAssets = rows.filter((row) => toNumber(row.allTrips) > 0 || toNumber(row.processed) > 0).length;
  const totalDays = getTotal(rows, "totalDays");
  const daysInUse = getTotal(rows, "daysInUse");
  const utilisation = totalDays > 0 ? (daysInUse / totalDays) * 100 : 0;
  const allTrips = getTotal(rows, "allTrips");
  const distance = getTotal(rows, "distance");
  const processed = getTotal(rows, "processed");
  const gsmLoss = getWeightedAverage(rows, "gsmLoss", "processed");
  const gpsLoss = getWeightedAverage(rows, "gpsLoss", "processed");
  const signalLossAverage = (gsmLoss + gpsLoss) / 2;
  const signalQuality = Math.max(0, Math.min(100, 100 - signalLossAverage));
  const signalAccent = signalLossAverage >= 20 ? "#cf2f45" : signalLossAverage >= 10 ? "#c07a16" : "#1f8a5b";
  const showTempA = rows.some((row) => (row.assetTempA !== undefined ? isFeatureEnabled(row.assetTempA) : toNumber(row.tempA) > 0));
  const showTempB = rows.some((row) => (row.assetTempB !== undefined ? isFeatureEnabled(row.assetTempB) : toNumber(row.tempB) > 0));
  const exceptionKeys = ["speed", "harshAccel", "harshBrake", "harshCorner", "duress", "powercut", "lowBattery", "parkedIdle", "ohs"];

  if (showTempA) {
    exceptionKeys.push("tempA");
  }

  if (showTempB) {
    exceptionKeys.push("tempB");
  }

  const exceptions = exceptionKeys.reduce((total, key) => total + getTotal(rows, key), 0);
  const exceptionSources = [
    { label: "Overspeed", value: getTotal(rows, "speed") },
    { label: "Powercut", value: getTotal(rows, "powercut") },
    { label: "Parked idle", value: getTotal(rows, "parkedIdle") },
    { label: "Low battery", value: getTotal(rows, "lowBattery") },
    { label: "OH&S", value: getTotal(rows, "ohs") },
    ...(showTempA ? [{ label: "Temp A", value: getTotal(rows, "tempA") }] : []),
    ...(showTempB ? [{ label: "Temp B", value: getTotal(rows, "tempB") }] : []),
  ].sort((a, b) => b.value - a.value);
  const topException = exceptionSources.find((source) => source.value > 0);

  return (
    <div className="assetSummaryMetricGrid">
      <MetricTile
        accent="#244b8f"
        icon="fa-calendar-check-o"
        label="Fleet Utilisation"
        value={formatPercent(utilisation)}
        detail={`${formatNumber(daysInUse)} of ${formatNumber(totalDays)} asset-days in use`}
      >
        <ProgressChart value={utilisation} color="#244b8f" />
      </MetricTile>

      <MetricTile
        accent="#1f8a5b"
        icon="fa-truck"
        label="Active Assets"
        value={`${formatNumber(activeAssets)} / ${formatNumber(assetCount)}`}
        detail={`${formatNumber(assetCount - activeAssets)} assets with no movement`}
        variant={activeAssets < assetCount ? "warning" : ""}
      >
        <MiniBars points={getPoints(rows, "allTrips")} color="#1f8a5b" />
      </MetricTile>

      <MetricTile
        accent="#2a7fbd"
        icon="fa-road"
        label="Distance"
        value={`${formatNumber(distance, 1)} km`}
        detail={`${formatNumber(allTrips ? distance / allTrips : 0, 1)} km per trip`}
      >
        <MiniSparkline points={getPoints(rows, "distance")} color="#2a7fbd" />
      </MetricTile>

      <MetricTile
        accent="#c07a16"
        icon="fa-location-arrow"
        label="Trips"
        value={formatNumber(allTrips)}
        detail={`${formatNumber(getTotal(rows, "trips"))} actual, ${formatNumber(getTotal(rows, "virtualTrips"))} virtual`}
      >
        <MiniBars points={getPoints(rows, "allTrips")} color="#c07a16" />
      </MetricTile>

      <MetricTile
        accent={signalAccent}
        icon="fa-signal"
        label="Signal Quality"
        value={`${formatPercent(signalQuality)} quality`}
        detail={`GSM loss ${formatPercent(gsmLoss)} | GPS loss ${formatPercent(gpsLoss)}`}
        variant={signalLossAverage >= 10 ? "warning" : ""}
      >
        <SignalQualityChart gsmLoss={gsmLoss} gpsLoss={gpsLoss} />
      </MetricTile>

      <MetricTile
        accent="#cf2f45"
        icon="fa-exclamation-triangle"
        label="Exceptions"
        value={formatNumber(exceptions)}
        detail={topException ? `${topException.label}: ${formatNumber(topException.value)}` : "No exception activity"}
        variant={exceptions > 0 ? "warning" : ""}
      >
        <MiniBars
          points={[
            getTotal(rows, "speed"),
            getTotal(rows, "harshAccel"),
            getTotal(rows, "harshBrake"),
            getTotal(rows, "harshCorner"),
            getTotal(rows, "powercut"),
            getTotal(rows, "lowBattery"),
          ]}
          color="#cf2f45"
        />
      </MetricTile>

      <MetricTile
        accent="#6b4fc4"
        icon="fa-database"
        label="Records"
        value={formatNumber(processed)}
        detail={`${formatNumber(assetCount ? processed / assetCount : 0, 0)} avg per asset`}
      >
        <MiniBars points={getPoints(rows, "processed")} color="#6b4fc4" />
      </MetricTile>
    </div>
  );
}

export default GroupSummaryMetricTiles;
